import React, { useState, useEffect, useCallback } from 'react';
import { X, Gear, Plus, Trash, PencilSimple, Play, TestTube } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

/**
 * MailRulesModal — beheer mail-regels (CRUD).
 *
 * Een regel = voorwaarden + acties die automatisch worden toegepast op inkomende mail.
 * Voorbeelden:
 *  - "from contains 'newsletter'" → mark_read + add_label "Nieuwsbrieven"
 *  - "subject starts_with 'Factuur'" → move_folder "Boekhouding"
 */

const VELD_OPTIES = [
  { v: 'from', l: 'Afzender (from)' },
  { v: 'subject', l: 'Onderwerp (subject)' },
  { v: 'body', l: 'Inhoud (body)' },
  { v: 'to', l: 'Ontvanger (to)' },
];

const OP_OPTIES = [
  { v: 'contains', l: 'bevat' },
  { v: 'equals', l: 'is exact' },
  { v: 'starts_with', l: 'begint met' },
  { v: 'ends_with', l: 'eindigt op' },
  { v: 'regex', l: 'regex match' },
];

const ACTIE_OPTIES = [
  { v: 'move_folder', l: 'Verplaats naar map', heeft_waarde: true, placeholder: 'INBOX, ARCHIEF, ...' },
  { v: 'mark_read', l: 'Markeer als gelezen', heeft_waarde: false },
  { v: 'flag_color', l: 'Vlag-kleur', heeft_waarde: true, placeholder: 'yellow, red, green, blue' },
  { v: 'add_label', l: 'Voeg label toe', heeft_waarde: true, placeholder: 'Nieuwsbrief, Klant-X, ...' },
  { v: 'delete', l: '⚠️ Verwijder', heeft_waarde: false },
];

const emptyRule = () => ({
  naam: '',
  actief: true,
  volgorde: 0,
  match_alle: true,
  voorwaarden: [{ veld: 'from', operator: 'contains', waarde: '' }],
  acties: [{ type: 'mark_read', waarde: '' }],
});

export default function MailRulesModal({ onClose }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editRule, setEditRule] = useState(null);  // null = list view, object = edit view

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/mail/rules');
      setRules(r.data?.items || []);
    } catch (e) {
      toast.error('Kon regels niet laden');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRule = async () => {
    if (!editRule.naam.trim()) { toast.error('Naam is verplicht'); return; }
    if (!editRule.voorwaarden.length) { toast.error('Minstens 1 voorwaarde'); return; }
    if (!editRule.acties.length) { toast.error('Minstens 1 actie'); return; }
    try {
      const payload = { ...editRule };
      delete payload.id; delete payload.tenant_id; delete payload.created_at; delete payload.updated_at; delete payload.matches_count;
      if (editRule.id) {
        await API.put(`/mail/rules/${editRule.id}`, payload);
      } else {
        await API.post('/mail/rules', payload);
      }
      toast.success(editRule.id ? 'Regel bijgewerkt' : 'Regel aangemaakt');
      setEditRule(null);
      load();
    } catch (e) {
      toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteRule = async (rid) => {
    if (!confirm('Regel verwijderen?')) return;
    try {
      await API.delete(`/mail/rules/${rid}`);
      toast.success('Verwijderd');
      load();
    } catch (e) { toast.error('Mislukt'); }
  };

  const testRule = async (rid) => {
    try {
      const r = await API.post(`/mail/rules/${rid}/test`);
      toast.success(`${r.data.matches_count} matches op ${r.data.total_checked} mails`);
    } catch (e) { toast.error('Test mislukt'); }
  };

  const runRule = async (rid) => {
    if (!confirm('Regel nu uitvoeren op INBOX-mails? Acties worden toegepast.')) return;
    try {
      const r = await API.post(`/mail/rules/${rid}/run`);
      toast.success(`${r.data.affected} mails verwerkt (${r.data.checked} gecontroleerd)`);
      load();
    } catch (e) { toast.error('Mislukt: ' + (e.response?.data?.detail || e.message)); }
  };

  // ── EDIT VIEW ──
  if (editRule) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditRule(null)} data-testid="mail-rule-edit-modal">
        <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-3 border-b bg-emerald-50 flex items-center justify-between">
            <h3 className="text-base font-bold">{editRule.id ? 'Regel bewerken' : 'Nieuwe regel'}</h3>
            <button onClick={() => setEditRule(null)} className="p-1 hover:bg-emerald-100 rounded"><X size={18} /></button>
          </div>
          <div className="overflow-y-auto flex-1 p-5 space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase text-gray-500">Naam</label>
              <input
                type="text"
                value={editRule.naam}
                onChange={e => setEditRule(r => ({ ...r, naam: e.target.value }))}
                placeholder="bv. Newsletter auto-archiveren"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm mt-1"
                data-testid="rule-naam"
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={editRule.actief} onChange={e => setEditRule(r => ({ ...r, actief: e.target.checked }))} data-testid="rule-actief" />
                Actief
              </label>
              <label className="text-xs inline-flex items-center gap-1">
                Volgorde:
                <input type="number" value={editRule.volgorde} onChange={e => setEditRule(r => ({ ...r, volgorde: parseInt(e.target.value, 10) || 0 }))} className="w-16 px-2 py-0.5 border border-gray-300 rounded text-xs" />
              </label>
              <label className="text-xs inline-flex items-center gap-1">
                <input type="radio" checked={editRule.match_alle === true} onChange={() => setEditRule(r => ({ ...r, match_alle: true }))} /> ALLE voorwaarden (EN)
              </label>
              <label className="text-xs inline-flex items-center gap-1">
                <input type="radio" checked={editRule.match_alle === false} onChange={() => setEditRule(r => ({ ...r, match_alle: false }))} /> 1 voorwaarde (OF)
              </label>
            </div>

            {/* Voorwaarden */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold uppercase text-gray-500">Voorwaarden (ALS)</label>
                <button
                  onClick={() => setEditRule(r => ({ ...r, voorwaarden: [...r.voorwaarden, { veld: 'from', operator: 'contains', waarde: '' }] }))}
                  className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                ><Plus size={12} /> Voorwaarde</button>
              </div>
              <div className="space-y-2">
                {editRule.voorwaarden.map((v, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`rule-voorwaarde-${i}`}>
                    <select value={v.veld} onChange={e => setEditRule(r => { const w = [...r.voorwaarden]; w[i] = { ...w[i], veld: e.target.value }; return { ...r, voorwaarden: w }; })} className="px-2 py-1 border border-gray-300 rounded text-xs">
                      {VELD_OPTIES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <select value={v.operator} onChange={e => setEditRule(r => { const w = [...r.voorwaarden]; w[i] = { ...w[i], operator: e.target.value }; return { ...r, voorwaarden: w }; })} className="px-2 py-1 border border-gray-300 rounded text-xs">
                      {OP_OPTIES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <input
                      type="text"
                      value={v.waarde}
                      onChange={e => setEditRule(r => { const w = [...r.voorwaarden]; w[i] = { ...w[i], waarde: e.target.value }; return { ...r, voorwaarden: w }; })}
                      placeholder="bv. newsletter"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    />
                    <button onClick={() => setEditRule(r => ({ ...r, voorwaarden: r.voorwaarden.filter((_, j) => j !== i) }))} className="text-red-500 hover:text-red-700"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Acties */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold uppercase text-gray-500">Acties (DAN)</label>
                <button
                  onClick={() => setEditRule(r => ({ ...r, acties: [...r.acties, { type: 'mark_read', waarde: '' }] }))}
                  className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                ><Plus size={12} /> Actie</button>
              </div>
              <div className="space-y-2">
                {editRule.acties.map((a, i) => {
                  const opt = ACTIE_OPTIES.find(o => o.v === a.type);
                  return (
                    <div key={i} className="flex gap-2 items-center" data-testid={`rule-actie-${i}`}>
                      <select value={a.type} onChange={e => setEditRule(r => { const ac = [...r.acties]; ac[i] = { ...ac[i], type: e.target.value }; return { ...r, acties: ac }; })} className="px-2 py-1 border border-gray-300 rounded text-xs">
                        {ACTIE_OPTIES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                      {opt?.heeft_waarde && (
                        <input
                          type="text"
                          value={a.waarde}
                          onChange={e => setEditRule(r => { const ac = [...r.acties]; ac[i] = { ...ac[i], waarde: e.target.value }; return { ...r, acties: ac }; })}
                          placeholder={opt.placeholder}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                        />
                      )}
                      <button onClick={() => setEditRule(r => ({ ...r, acties: r.acties.filter((_, j) => j !== i) }))} className="text-red-500 hover:text-red-700"><Trash size={14} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
            <button onClick={() => setEditRule(null)} className="btn btn-secondary btn-sm">Annuleer</button>
            <button onClick={saveRule} className="btn btn-primary btn-sm" data-testid="rule-save">Bewaren</button>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="mail-rules-modal">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b bg-emerald-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gear size={20} weight="duotone" className="text-emerald-700" />
            <div>
              <h3 className="text-base font-bold text-gray-900">Mail-regels</h3>
              <p className="text-xs text-gray-600 mt-0.5">Automatische verwerking van inkomende mail</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-emerald-100 rounded"><X size={18} /></button>
        </div>

        <div className="px-5 py-2 border-b flex items-center justify-between">
          <span className="text-xs text-gray-600">{rules.length} regel(s)</span>
          <button
            onClick={() => setEditRule(emptyRule())}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded inline-flex items-center gap-1"
            data-testid="rule-new"
          ><Plus size={12} weight="bold" /> Nieuwe regel</button>
        </div>

        <div className="overflow-y-auto flex-1 p-4">
          {loading && <div className="text-center text-sm text-gray-500 py-8">Laden...</div>}
          {!loading && rules.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Gear size={48} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nog geen regels — voeg er een toe om mails automatisch te verwerken.</p>
            </div>
          )}
          {!loading && rules.length > 0 && (
            <div className="space-y-2">
              {rules.map(r => (
                <div key={r.id} className="border border-gray-200 rounded p-3 hover:bg-gray-50" data-testid={`rule-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 inline-flex items-center gap-2">
                        {r.naam}
                        {!r.actief && <span className="text-[10px] uppercase bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">Uit</span>}
                        {r.actief && <span className="text-[10px] uppercase bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Actief</span>}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {r.voorwaarden?.length || 0} voorwaarde(n) {r.match_alle ? '(ALLE)' : '(MINSTENS 1)'} →
                        {' '}{r.acties?.length || 0} actie(s)
                        {r.matches_count > 0 && <span className="ml-2 text-emerald-700">· {r.matches_count} treffers</span>}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => testRule(r.id)} className="p-1.5 hover:bg-blue-100 text-blue-700 rounded" title="Test op INBOX (geen actie)" data-testid={`rule-test-${r.id}`}><TestTube size={14} /></button>
                      <button onClick={() => runRule(r.id)} className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded" title="Voer nu uit" data-testid={`rule-run-${r.id}`}><Play size={14} weight="fill" /></button>
                      <button onClick={() => setEditRule({ ...r })} className="p-1.5 hover:bg-gray-200 rounded" title="Bewerk" data-testid={`rule-edit-${r.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => deleteRule(r.id)} className="p-1.5 hover:bg-red-100 text-red-600 rounded" title="Verwijder" data-testid={`rule-delete-${r.id}`}><Trash size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
